// Heritage city landing pages. Lokala SEO-sidor för Fenny Heritage per stad.
// Återanvänder Section, QuickAnswer, SignatureCta för konsistens.

import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { breadcrumbs, faqPage, localBusinessForCity, aeoGeo } from "@/lib/seo/schema";
import { getCity } from "@/lib/seo/cities";

export interface HeritageCityCopy {
  citySlug: string;
  /** Lokal vinkel — varför Heritage är relevant just här. */
  angle: string;
  /** Typiska lokala användningsfall. */
  localUseCases: string[];
}

const FAQ_BASE = (cityName: string) => [
  {
    q: `Var spelar ni in livsberättelser i ${cityName}?`,
    a: `Vi spelar in hemma hos personen, på kontoret, i studio eller på en plats med personlig betydelse i ${cityName} och kringliggande orter. Vi tar med all utrustning.`,
  },
  {
    q: `Kan ni hjälpa företag i ${cityName} med kunskapsöverföring?`,
    a: `Ja. Vi gör strukturerade filmade intervjuer med nyckelpersoner, grundare och seniora medarbetare i ${cityName}-baserade företag — för onboarding, intern utbildning, generationsskifte och kulturarbete.`,
  },
  {
    q: `Hur lång tid tar en heritage-produktion?`,
    a: `Från första samtal till färdig film tar processen oftast 4–8 veckor, beroende på upplägg, antal intervjuer och hur mycket arkivmaterial som ska bearbetas.`,
  },
  {
    q: `Vad kostar en filmad livsberättelse i ${cityName}?`,
    a: `Priset beror på upplägg och omfattning. Mindre intervjuupplägg startar i den lägre prisklassen för Prime-produktioner, mer omfattande arv ligger högre. Vi lämnar offert efter första samtalet.`,
  },
  {
    q: `Kan filmen hållas privat inom familjen eller företaget?`,
    a: `Ja. Filmen levereras digitalt direkt till er. Ni bestämmer själva vem som får se den.`,
  },
];

export function heritageCityRoute(copy: HeritageCityCopy) {
  const city = getCity(copy.citySlug);
  if (!city) throw new Error(`Unknown city ${copy.citySlug}`);
  const path = `/heritage/${city.slug}`;
  const cityName = city.name;
  const title = `Heritage ${cityName} — livsberättelser på film | Fenny`;
  const description = `Filmade livsberättelser för familjer och kunskapsarv för företag i ${cityName} och ${city.regionLong}. Professionella intervjuer som bevarar berättelser och erfarenhet.`;
  const faq = FAQ_BASE(cityName);

  return {
    head: () => ({
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `https://fenny.se${path}` },
      ],
      links: [
        { rel: "canonical", href: `https://fenny.se${path}` },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path, name: title, description })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            localBusinessForCity({
              city: cityName,
              path,
              description: `Fenny Heritage i ${cityName} — filmade livsberättelser och kunskapsarv.`,
              postalRegion: city.region,
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqPage(faq)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Heritage", path: "/heritage" },
              { name: cityName, path },
            ]),
          ),
        },
      ],
    }),
    component: () => <HeritageCityPage city={city} copy={copy} faq={faq} path={path} />,
  };
}

function HeritageCityPage({
  city,
  copy,
  faq,
  path,
}: {
  city: NonNullable<ReturnType<typeof getCity>>;
  copy: HeritageCityCopy;
  faq: { q: string; a: string }[];
  path: string;
}) {
  return (
    <article className="pt-28">
      <Section className="pb-10">
        <div className="max-w-4xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">
            Fenny Heritage · {city.name}
          </div>
          <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
            Livsberättelser och kunskapsarv i {city.name}
          </h1>
          <p className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl" data-speakable>
            {copy.angle}
          </p>
          <QuickAnswer
            className="mt-6"
            text={`Fenny Heritage hjälper familjer och företag i ${city.name} och ${city.regionLong} att bevara livsberättelser, erfarenhet och tyst kunskap genom professionellt filmade intervjuer.`}
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-background hover:opacity-90"
            >
              Boka ett första samtal <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/heritage"
              className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-3 text-sm font-medium hover:bg-foreground/[0.05]"
            >
              Läs mer om Heritage
            </Link>
          </div>
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Två spår</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <Link
            to="/heritage/livsberattelser"
            className="group flex flex-col rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-7 transition hover:border-foreground/30 hover:bg-foreground/[0.06]"
          >
            <div className="text-xs uppercase tracking-widest text-mint">För familjer och privatpersoner</div>
            <h3 className="mt-3 text-2xl font-semibold md:text-3xl">Livsberättelser</h3>
            <p className="mt-3 text-pretty text-foreground/80">
              Filmade intervjuer där en person får berätta om sitt liv, sin uppväxt, familj, arbete och viktiga minnen. Bevaras för kommande generationer.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-mint">
              Läs mer
              <ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
            </span>
          </Link>
          <Link
            to="/heritage/kunskapsarv"
            className="group flex flex-col rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-7 transition hover:border-foreground/30 hover:bg-foreground/[0.06]"
          >
            <div className="text-xs uppercase tracking-widest text-mint">För företag och organisationer</div>
            <h3 className="mt-3 text-2xl font-semibold md:text-3xl">Kunskapsarv</h3>
            <p className="mt-3 text-pretty text-foreground/80">
              Strukturerade filmade intervjuer med nyckelpersoner, grundare och seniora medarbetare för onboarding, intern utbildning och dokumentation.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-mint">
              Läs mer
              <ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
            </span>
          </Link>
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Typiska uppdrag i {city.name}</h2>
        <ul className="mt-6 grid gap-3 md:grid-cols-2 text-foreground/85">
          {copy.localUseCases.map((u) => (
            <li key={u} className="flex gap-3">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-mint" />
              <span>{u}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Vanliga frågor</h2>
        <div className="mt-8 max-w-3xl space-y-6">
          {faq.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/75">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <RelatedLinksRail sourcePath={path} />
      <SignatureCta />
    </article>
  );
}
